import {
  Check,
  EmailOutlined,
  LockOpen,
  OpenInNewOutlined,
  PhoneOutlined,
  PlayCircleOutline,
  QrCode2,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';

import { ISigner } from '../../../types/Parties';
import { formatPartyName, phoneObjToString } from '../../utils/helpers';

interface IProps {
  signer: ISigner;
  signingDelegator?: ISigner
  sessionActive: boolean;
  linkDisabled: boolean;
  completed: boolean;
  signingPrerequisitesMet: boolean;
  loading: boolean;
  activateSession: () => void;
  viewQR: () => void;
  signDocs: () => void;
  unlockParty: () => void;
}

export default function IPENSessionSigner(props: IProps) {
  const {
    signer,
    signingDelegator,
    sessionActive,
    completed,
    signingPrerequisitesMet,
    loading,
    viewQR,
    activateSession,
    signDocs,
    unlockParty,
    linkDisabled,
  } = props;

  const getSignerName = (): string => {
      const formattedSignerName = formatPartyName({
          FirstName: signer?.FirstName,
          LastName: signer?.LastName,
          MiddleName: signer?.MiddleName,
          Suffix: signer?.Suffix,
      }, 'signer')

      if (!signingDelegator) {
          return formattedSignerName;
      }

      const formattedSigningDelegatorName = formatPartyName({
          FirstName: signingDelegator.FirstName,
          LastName: signingDelegator.LastName,
          MiddleName: signingDelegator.MiddleName,
          Suffix: signingDelegator.Suffix,
      }, 'signer')

      return `${formattedSigningDelegatorName} by ${formattedSignerName} (POA)`;
  }

  return (
    <Card
      raised={!completed && sessionActive}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: (theme) => theme.spacing(2),
        margin: (theme) => theme.spacing(2),
        marginTop: '0px',
        borderLeft:
          sessionActive && !completed ? `10px solid ${green[500]}` : 'none',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='button'>
          {getSignerName()}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {Array.isArray(signer?.Emails) && signer?.Emails.length > 0 && (
            <Button
              startIcon={<EmailOutlined />}
              size='small'
              style={{ textTransform: 'none' }}>
              {signer?.Emails[0] ? signer?.Emails[0] : ''}
            </Button>
          )}
          {Array.isArray(signer?.PhoneNumbers) &&
            signer?.PhoneNumbers.length > 0 && (
              <Button startIcon={<PhoneOutlined />} size='small'>
                {signer?.PhoneNumbers[0]
                  ? phoneObjToString(signer?.PhoneNumbers[0])
                  : ''}
              </Button>
            )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {loading ? (
          <CircularProgress size='26px' />
        ) : !completed ? (
          signingPrerequisitesMet ? (
            sessionActive ? (
              <>
                <IconButton onClick={viewQR}>
                  <QrCode2 />
                </IconButton>
                <IconButton
                  disabled={!linkDisabled}
                  color={linkDisabled ? 'error' : 'default'}
                  onClick={unlockParty}>
                  <LockOpen />
                </IconButton>
                <Divider
                  orientation='vertical'
                  variant='middle'
                  flexItem
                  sx={{ my: 0, mx: 2 }}
                />
                <Typography
                  variant='button'
                  sx={{ marginRight: '15px', color: green[500] }}>
                  {!linkDisabled ? 'Active Session' : 'Reset Login'}
                </Typography>
                <Button
                  onClick={signDocs}
                  endIcon={<OpenInNewOutlined />}
                  variant='contained'
                  sx={{ background: green[500] }}
                  disableElevation>
                  Review & Sign
                </Button>
              </>
            ) : (
              <>
                <IconButton disabled onClick={viewQR}>
                  <QrCode2 />
                </IconButton>
                <IconButton
                  disabled={!linkDisabled}
                  color={linkDisabled ? 'error' : 'default'}
                  onClick={unlockParty}>
                  <LockOpen />
                </IconButton>
                <Divider
                  orientation='vertical'
                  variant='middle'
                  flexItem
                  sx={{ my: 0, mx: 2 }}
                />
                <Button
                  onClick={activateSession}
                  startIcon={<PlayCircleOutline />}
                  variant='contained'
                  disableElevation>
                  Activate Session
                </Button>
              </>
            )
          ) : (
            <>
              <IconButton onClick={viewQR}>
                <QrCode2 />
              </IconButton>
              <IconButton
                disabled={!linkDisabled}
                color={linkDisabled ? 'error' : 'default'}
                onClick={unlockParty}>
                <LockOpen />
              </IconButton>
              <Divider
                orientation='vertical'
                variant='middle'
                flexItem
                sx={{ my: 0, mx: 2 }}
              />
              <Typography
                variant='button'
                sx={{
                  marginRight: '15px',
                  color: (theme) => theme.palette.primary.main,
                }}>
                Awaiting Signatures
              </Typography>
            </>
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <Check />
            <Typography
              variant='button'
              sx={{ color: (theme) => theme.palette.primary.main }}>
              Completed
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}
